import {AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild} from '@angular/core';
import {filter} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Chart, ChartConfiguration, Color, Point, ScriptableScaleContext} from 'chart.js';
import annotationPlugin, {AnnotationOptions} from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import {MathUtils, Utils} from '@astek/utils';
import {RxUtils} from '../../../../shared/utils/rx-utils';
import {ConfigService} from '../../../../core/services/config/config.service';
import {PressureGraphConfig} from '../../../../core/services/config/pressure-graph-config';
import {PressureUnitEnum} from '../../../../core/services/state/pressure-unit.enum';
import {ThemeService} from '../../../../core/services/theme/theme.service';
import {chartAreaStylePlugin} from '../../services/graph/chart-area-style.plugin';
import {PressureUnitUtils} from '../../../../core/utils/pressure-unit-utils';
import {GraphService} from '../../services/graph/graph.service';
import {SharedUtils} from '../../../../shared/utils/shared-utils';
import {commentsIndicatorsPlugin} from '../../services/graph/comments-indicators.plugin';
import {MeasureTypeEnum} from '../../../../core/services/state/measure-type.enum';
import {PressureScaleService} from '../../services/pressure-scale/pressure-scale.service';
import {PatientData} from '../../services/data/patient-data';

const ZOOM_PRECISION = 1e-2;

@Component({
  selector: 'xp-raw-pressure-graph',
  templateUrl: './raw-pressure-graph.component.html',
  styleUrls: [
    '../graph.common.scss',
    './raw-pressure-graph.component.scss',
  ],
  providers: [
    GraphService,
    PressureScaleService,
  ],
})
export class RawPressureGraphComponent implements AfterViewInit, OnDestroy {

  @Input({required: true})
  public patientData!: PatientData;

  public readonly initialConfig: ChartConfiguration<'line', Array<Point>> = {
    type: 'line',
    data: {
      datasets: [
        {
          data: [],
          fill: false,
          pointStyle: false,
          spanGaps: true,
          cubicInterpolationMode: 'monotone',
        },
      ],
    },
    options: {
      // Turn off animations and data parsing for performance
      animation: false,
      parsing: false,
      normalized: true,
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'x',
      events: [],
      plugins: {
        legend: {display: false},
        annotation: {
          animations: {},
          annotations: [],
        },
        zoom: {
          limits: {},
          zoom: {
            mode: 'xy',
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            drag: {
              enabled: false,
            },
            onZoom: () => this.onZoom(),
          },
          pan: {
            enabled: true,
            mode: 'xy',
          },
        },
        chartAreaBorder: {},
        commentsIndicators: {},
        decimation: {
          enabled: true,
          algorithm: 'min-max',
        },
      },
    },
    plugins: [
      annotationPlugin,
      zoomPlugin,
      chartAreaStylePlugin,
      commentsIndicatorsPlugin,
    ],
  };

  @ViewChild('chartCanvas')
  public canvas!: ElementRef<HTMLCanvasElement>;
  public chart!: Chart<'line', Array<Point>>;

  private _readyToUpdate: boolean = false;

  private _maxTime: number = 0;


  constructor(
    private readonly _elementRef: ElementRef<HTMLElement>,
    private readonly _configService: ConfigService,
    private readonly _themeService: ThemeService,
    private readonly _graphService: GraphService,
    private readonly _pressureScaleService: PressureScaleService,
  ) {
    RxUtils.observeDoubleClick(this._elementRef.nativeElement).pipe(
      takeUntilDestroyed(),
      filter(() => this.isZoomedOrPanned),
    ).subscribe(() => {
      this.resetZoom();
    });
    this._themeService.darkThemeChanged.pipe(
      takeUntilDestroyed(),
      filter(() => Utils.isNotNullOrUndefined(this.chart)),
    ).subscribe(() => {
      this.chart.data.datasets[0].borderColor = this.graphConfig.curve.color.fromTheme(this.darkTheme);
      this.updateChartAreaStyle();
      this.updateTimeScale();
      this.updatePressureScale();
      this.updateAnnotations();
      this.update();
    });
  }

  public get unit(): PressureUnitEnum {
    return this.patientData.pressureUnit;
  }

  public get measureType(): MeasureTypeEnum {
    return this.patientData.measureType;
  }

  public get isZoomedOrPanned(): boolean {
    return this._readyToUpdate && (
      (this.chart?.isZoomedOrPanned() ?? false)
      || this.isXAxisZoomedOrPanned
      || this.isYAxisZoomedOrPanned
    );
  }

  public get isXAxisZoomedOrPanned(): boolean {
    return this._readyToUpdate && (
      !MathUtils.fuzzyEquals(
        (this.chart.scales['x'].options.min as number),
        this.graphConfig.timeMinInterval.min,
        ZOOM_PRECISION,
      ) || !MathUtils.fuzzyEquals(
        (this.chart.scales['x'].options.max as number),
        this._maxTime,
        ZOOM_PRECISION,
      )
    );
  }

  public get isYAxisZoomedOrPanned(): boolean {
    return this._readyToUpdate && (
      !MathUtils.fuzzyEquals(
        (this.chart.scales['y'].options.min as number),
        this._pressureScaleService.boundariesWithMargin.minPressure,
        ZOOM_PRECISION,
      ) || !MathUtils.fuzzyEquals(
        (this.chart.scales['y'].options.max as number),
        this._pressureScaleService.boundariesWithMargin.maxPressure,
        ZOOM_PRECISION,
      )
    );
  }

  public get xAxisZoomLevel(): number {
    if (!this._readyToUpdate) {
      return 1;
    }
    const currentMin = this.chart.scales['x'].options.min as number;
    const normalMin = 0;
    const currentMax = (this.chart.scales['x'].options.max as number);
    const normalMax = this._maxTime;
    return (normalMax - normalMin) / (currentMax - currentMin);
  }

  public get yAxisZoomLevel(): number {
    if (!this._readyToUpdate) {
      return 1;
    }
    const currentMin = this.chart.scales['y'].options.min as number;
    const normalMin = this._pressureScaleService.boundariesWithMargin.minPressure;
    const currentMax = (this.chart.scales['y'].options.max as number);
    const normalMax = this._pressureScaleService.boundariesWithMargin.maxPressure;
    return (normalMax - normalMin) / (currentMax - currentMin);
  }

  public get darkTheme(): boolean {
    return this._themeService.darkTheme;
  }

  private get graphConfig(): PressureGraphConfig {
    return this._configService.config.measurements.rawPressureGraph;
  }

  public ngAfterViewInit(): void {
    this._graphService.init(this.patientData);
    this._pressureScaleService.init(this.patientData, this.graphConfig, true);

    // Use chart.js directly instead of ng2-charts to improve performance when the measure is updated in real time
    this.chart = new Chart(this.canvas.nativeElement, this.initialConfig);

    this.chart.data.datasets[0].borderColor = this.graphConfig.curve.color.fromTheme(this.darkTheme);
    this.chart.data.datasets[0].borderWidth = this.graphConfig.curve.thickness;
    this.updateChartAreaStyle();
    this.clearGraph();
    this.toggleZoomPan(true);
    this.updateCurve();
    if (Utils.isNotNullOrUndefined(this._graphService.lastMeasurement)) {
      this._maxTime = Math.max(
        this._maxTime,
        this._graphService.timestampToSeconds(this._graphService.lastMeasurement.timestamp),
      );
    }
    this.resetZoom();

    this._readyToUpdate = true;
    this.update();
  }

  public ngOnDestroy(): void {
    this._readyToUpdate = false;
    this.chart.destroy();
  }

  public onZoom(): void {
    this.updateAnnotations();
  }

  public handleResetZoom(event: MouseEvent): void {
    event.stopPropagation();
    this.resetZoom();
  }

  public resetZoom(): void {
    this.chart.resetZoom();
    this.chart.scales['x'].options.min = this.graphConfig.timeMinInterval.min;
    this.chart.scales['x'].options.max = this._maxTime;
    this.chart.options.plugins!.zoom!.limits!['x']!.min = 0;
    this.chart.options.plugins!.zoom!.limits!['x']!.max = this._maxTime;

    const pressureBoundaries = this._pressureScaleService.boundariesWithMargin;
    this.chart.scales['y'].options.min = pressureBoundaries.minPressure;
    this.chart.scales['y'].options.max = pressureBoundaries.maxPressure;
    this.chart.options.plugins!.zoom!.limits!['y']!.min = pressureBoundaries.minPressure;
    this.chart.options.plugins!.zoom!.limits!['y']!.max = pressureBoundaries.maxPressure;

    this.updateAnnotations();
    this.update();
  }

  public update(): void {
    if (Utils.isNullOrUndefined(this.chart) || !this._readyToUpdate) {
      return;
    }
    this.chart.update();
  }

  private updateChartAreaStyle(): void {
    this.chart.options.plugins!.chartAreaBorder = {
      backgroundColors: this.graphConfig.background.map(color => color.fromTheme(this.darkTheme)),
      borderColor: this.graphConfig.border.color.fromTheme(this.darkTheme),
      borderWidth: this.graphConfig.border.width,
      borderRadius: this.graphConfig.border.radius,
    };
  }

  private updateTimeScale(
    min: number = (this.chart.options.scales!['x']?.min ?? 0) as number,
    max: number = (this.chart.options.scales!['x']?.max ?? 0) as number,
  ): void {
    this._graphService.setupAxisScale(
      this.chart,
      this.graphConfig,
      'x',
      'bottom',
      min,
      max,
      this.graphConfig.maxZoom.time,
      this.graphConfig.graduations.timeStepSize.labels[0].value,
      'center',
      (tickValue: number | string): string | null => {
        if (Utils.isString(tickValue)) {
          return tickValue;
        }
        const tickOffset = tickValue - this.graphConfig.timeMinInterval.min;
        const minTime = this.chart.scales['x'].options.min as number;
        const maxTime = this.chart.scales['x'].options.max as number;
        const displayedRange = maxTime - minTime;
        const relativeThreshold = 0.04;
        // only display relevant timestamps
        if (
          MathUtils.fuzzyEquals(tickOffset, minTime, relativeThreshold * displayedRange)
          || MathUtils.fuzzyEquals(tickOffset, maxTime, relativeThreshold * displayedRange)
        ) {
          return '';
        }
        return SharedUtils.secondsToString(
          MathUtils.roundStep(tickOffset, this.graphConfig.graduations.timeStepSize.labels[0].value),
        );
      },
      (context: ScriptableScaleContext): Color => {
        const currentScaleRange = context.scale.max - context.scale.min;
        const currentTimeStep = this.graphConfig.graduations.timeStepSize.labels.find(
          (timeStep) => {
            return Utils.isNullOrUndefined(timeStep.maxRange) || (timeStep.maxRange > currentScaleRange);
          },
        );
        if (
          Utils.isNotNullOrUndefined(context.tick) && (Utils.isNotNullOrUndefined(currentTimeStep)
            && ((context.tick.value - this.graphConfig.timeMinInterval.min) % currentTimeStep.value !== 0))
        ) {
          return 'transparent';
        }
        let minTime = this.graphConfig.timeMinInterval.min;
        if (Utils.isNotNullOrUndefined(this._graphService.firstMeasurement)) {
          minTime = Math.min(
            minTime,
            this._graphService.timestampToSeconds(this._graphService.firstMeasurement.timestamp),
          );
        }
        let maxTime = this.graphConfig.timeMinInterval.max;
        if (Utils.isNotNullOrUndefined(this._graphService.lastMeasurement)) {
          maxTime = Math.max(
            maxTime,
            this._graphService.timestampToSeconds(this._graphService.lastMeasurement.timestamp),
          );
        }
        if (Utils.isNotNullOrUndefined(context.tick) && (
          (context.tick.value < minTime) || (context.tick.value > maxTime)
        )) {
          return this.graphConfig.graduations.outOfRangeColor.fromTheme(this.darkTheme);
        }
        return this.graphConfig.graduations.color.fromTheme(this.darkTheme);
      },
      (): Color => {
        return 'transparent';
      },
    );
  }

  private updatePressureScale(
    min: number = (this.chart.options.scales!['y']?.min ?? 0) as number,
    max: number = (this.chart.options.scales!['y']?.max ?? 0) as number,
  ): void {
    this._graphService.setupAxisScale(
      this.chart,
      this.graphConfig,
      'y',
      'left',
      min,
      max,
      this.graphConfig.maxZoom.pressure.fromUnit(this.unit),
      this._pressureScaleService.pressureLineStepSize,
      'center',
      null,
      (context: ScriptableScaleContext): Color => {
        if (
          Utils.isNotNullOrUndefined(context.tick)
          && (
            context.tick.value % this._pressureScaleService.pressureLabelStepSize !== 0
            || MathUtils.fuzzyEquals(
              context.tick.value,
              this._pressureScaleService.boundariesWithMargin.minPressure,
            )
            || MathUtils.fuzzyEquals(
              context.tick.value,
              this._pressureScaleService.boundariesWithMargin.maxPressure,
            )
          )
        ) {
          return 'transparent';
        }
        return this.graphConfig.graduations.color.fromTheme(this.darkTheme);
      },
      (context: ScriptableScaleContext): Color => {
        if (
          Utils.isNotNullOrUndefined(context.tick)
          && (
            context.tick.value % this._pressureScaleService.pressureLineStepSize !== 0
            || MathUtils.fuzzyEquals(
              context.tick.value,
              this._pressureScaleService.boundariesWithMargin.minPressure,
            )
            || MathUtils.fuzzyEquals(
              context.tick.value,
              this._pressureScaleService.boundariesWithMargin.maxPressure,
            )
          )
        ) {
          return 'transparent';
        }
        return this.graphConfig.graduations.lines.color.fromTheme(this.darkTheme);
      },
    );
  }

  private updateAnnotations(): void {
    const annotations: Array<AnnotationOptions> = [];

    //!\\ the annotations must be set in a single assignment, pushing into the array will not work for some reason
    this.chart.options.plugins!.annotation!.annotations = annotations;
  }

  private clearGraph(): void {
    this._maxTime = Utils.isNotNullOrUndefined(this.patientData.lastMeasurement)
      ? this._graphService.timestampToSeconds(this.patientData.lastMeasurement.timestamp)
      : this.graphConfig.timeMinInterval.max;
    this.updateTimeScale(this.graphConfig.timeMinInterval.min, this._maxTime);
    this.resetPressureScale();
    this.chart.options.plugins!.annotation!.annotations = [];
    this.chart.data.datasets[0].data = [];
  }

  private resetPressureScale(): void {
    this._pressureScaleService.reset();
    this.updatePressureScale(
      this._pressureScaleService.boundariesWithMargin.minPressure,
      this._pressureScaleService.boundariesWithMargin.maxPressure,
    );
  }

  private updateCurve(): void {
    this.chart.data.datasets[0].data = this.patientData.measurements
      .map((measurement) => {
        this._pressureScaleService.updateBoundaryFromValue(measurement.rawPressure);
        return {
          x: this._graphService.timestampToSeconds(measurement.timestamp),
          y: PressureUnitUtils.convertMmHgToUnit(measurement.rawPressure, this.unit),
        };
      });
  }

  private toggleZoomPan(enabled: boolean): void {
    this.chart.options.plugins!.zoom!.zoom!.wheel!.enabled = enabled;
    this.chart.options.plugins!.zoom!.zoom!.pinch!.enabled = enabled;
    this.chart.options.plugins!.zoom!.pan!.enabled = enabled;
  }

}
